import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import useMerchandiseBrandsList from 'hooks/Merchandise/useMerchandiseBrandsList'
import useMerchandiseBrands from 'hooks/Merchandise/useMerchandiseBrands'
import { getMerchandiseBrandSearchUrl } from '../../merchandiseUtils'
import Sheet from 'components/utils/Sheet'
import { merchandiseMegaMenuBreakpoints } from './merchandiseMegaMenuBreakpoints'
import { useMegaMenuTracking } from 'analytics/snowplow/events/megaMenuEvents'

interface ContainerProps {
  $layout?: 'desktop' | 'mobile'
  $panels?: number
}

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  padding-block: ${props => props.$layout === 'mobile' ? rem(0) : rem(24)};
  padding-left: ${props => {
    if (props.$layout === 'mobile') return rem(8)
    return props.$panels === 2 ? rem(36) : rem(24)
  }};
  padding-right: ${props => props.$layout === 'mobile' ? rem(8) : rem(24)};
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

interface BrandGridProps {
  $columns: number
  $panels: number
}

const BrandGrid = styled.div<BrandGridProps>`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns}, 1fr);
  gap: ${rem(12)};
  width: 100%;

  ${merchandiseMegaMenuBreakpoints.mediumLarge} {
    grid-template-columns: repeat(${props => Math.min(props.$columns, 2 * props.$panels)}, 1fr);
  }
`

const BrandLogoContainer = styled(Sheet)`
  aspect-ratio: 120 / 67.5;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    box-shadow: inset 0px 0px 0px 1px ${props => props.theme.palette.neutral.default.one};
  }
`

interface Props {
  categoryId?: string
  /**
   * Number of panels this component spans (affects responsive breakpoint)
   * @default 1
   */
  panels?: number
  /**
   * Number of columns in the grid
   * @default 3
   */
  columns?: number
  /**
   * Maximum number of brands to display
   * @default 12
   */
  limit?: number
  /**
   * Minimum number of brands required to show the panel
   * @default 6
   */
  minBrands?: number
  /**
   * Layout mode for positioning "View All Brands"
   * - desktop: Shows "View All Brands" at the bottom
   * - mobile: Shows "View All Brands" in the header on the right
   * @default 'desktop'
   */
  layout?: 'desktop' | 'mobile'
  /**
   * Callback to close the dropdown
   */
  onClose?: () => void
}

function MerchandiseMegaMenuFeaturedBrands(props: Props) {
  const {
    categoryId,
    panels = 1,
    columns = 3,
    limit = 12,
    minBrands = 6,
    layout = 'desktop',
    onClose,
  } = props

  const brandFilters = useMemo((): App.MerchandiseBrandFilters => ({
    ...(categoryId && { categories: [categoryId] }),
    sortBy: 'top',
    limit,
  }), [categoryId, limit])

  const [brandIds] = useMerchandiseBrandsList(brandFilters)
  const [brands] = useMerchandiseBrands(brandIds.slice(0, limit))

  const trackBrandClick = useMegaMenuTracking('brand', onClose)
  const trackViewAllClick = useMegaMenuTracking('view_all_brands', onClose)
  const viewAllUrl = categoryId ? `/search/shop?categories=${categoryId}` : '/shop/brands'

  if (brands.length < minBrands) {
    return null
  }

  return <Container $layout={layout} $panels={panels}>
    <Header>
      <BodyText variant="medium" colour="neutral-two">
        Featured Brands
      </BodyText>
      {layout === 'mobile' && (
        <TextLink
          to={viewAllUrl}
          onClick={() => trackViewAllClick({ id: 'view_all_brands', name: 'View All Brands' })}
          colour="neutral-two"
          size="medium"
          weight="regular"
        >
          View All Brands
        </TextLink>
      )}
    </Header>
    <BrandGrid $columns={columns} $panels={panels}>
      {brands.map((brand, index) => (
        <Clickable
          key={brand.id}
          to={getMerchandiseBrandSearchUrl(brand)}
          onClick={() => trackBrandClick(brand, index)}
        >
          <BrandLogoContainer
            border="neutral-six"
            borderRadius="M"
            background="neutral-eight"
            padding={8}
          >
            {brand.logo && <Image
              fit="center"
              image={brand.logo}
              objectFit="contain"
              width={120}
            />}
          </BrandLogoContainer>
        </Clickable>
      ))}
    </BrandGrid>
    {layout === 'desktop' && panels !== 2 && (
      <TextLink
        to={viewAllUrl}
        onClick={() => trackViewAllClick({ id: 'view_all_brands', name: 'View All Brands' })}
        colour="neutral-two"
        size="medium"
        weight="regular"
      >
        View All Brands
      </TextLink>
    )}
  </Container>
}

export default MerchandiseMegaMenuFeaturedBrands
